import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../services/services';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import Table from '../../../components/common/table/table';
import css from './index.module.css';
import ExcelButton from '../../../components/common/table/excel-button/excel-button';
import {
  CROP_CHANGE_PLAN_CREATE,
  CROP_CHANGE_PLAN_VIEW,
} from '../../../router/routes';
import { can, route } from '../../../services/utils';
import { LinkComponent as Link } from '../../../components/form/elements/link';
import Field from '../../../interfaces/field';
import ShowIfAllowed from '../../../components/common/show-if-allowed';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import Title from '../../../components/common/title';

export default function CropChangePlan() {
  const { cropChangePlanService } = useService();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const selectedPlanState = useState<any>([]);

  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.crop_change_plan.fields.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      cropChangePlanService.index(farmId, season, {
        page,
        sort,
        desc,
        ...filter,
        getPreviousSeasonCropChangePlans: true,
      }),
    [cropChangePlanService, farmId, season]
  );

  const toolbar = useCallback(() => {
    return (
      <div className={css['toolbar']}>
        <ShowIfAllowed ability={{ title: 'create crop_change_plan' }}>
          <div>
            <Link
              label="Plānot"
              primary={true}
              linkTo={route(
                CROP_CHANGE_PLAN_CREATE.concat('?fieldIds=:fieldIds'),
                {
                  farmId,
                  season,
                  fieldIds: selectedPlanState[0]
                    ?.filter(
                      (plan: any) =>
                        !plan.crop_change_plan?.hasOwnProperty('id')
                    )
                    .map((field: Field) => field.id),
                }
              )}
              icon={faPlus}
            />
          </div>
        </ShowIfAllowed>
        <ShowIfAllowed ability={{ title: 'view crop_change_plan' }}>
          <div className="my-auto">
            <ExcelButton
              onClick={() => {
                return cropChangePlanService
                  .exportPlanXls(farmId, season)
                  .subscribe();
              }}
            />
          </div>
        </ShowIfAllowed>
      </div>
    );
  }, [farmId, season, selectedPlanState, cropChangePlanService]);

  const viewRoute = useCallback(
    (row: any) => {
      if (
        row.crop_change_plan &&
        row.crop_change_plan.season_id === parseInt(season) &&
        can(farmUser, { title: 'view crop_change_plan' })
      ) {
        return route(CROP_CHANGE_PLAN_VIEW.concat('?fieldIds=:fieldIds'), {
          farmId: farmId,
          season: season,
          id: row.crop_change_plan.id,
          fieldIds: [row.crop_change_plan.field_id],
        });
      }
    },
    [farmId, season, farmUser]
  );

  return (
    <>
      <Title title={t('pages.crop.crop_change_plan.title')}></Title>
      <div className={css['crop-change-plan-table']}>
        <Table
          toolbar={toolbar}
          request={request}
          keyAccessor="id"
          columns={[
            {
              accessor: 'lad_number',
              header: tp('lad_number'),
              className: css['strong'],
            },
            {
              accessor: 'name',
              header: tp('field_name'),
              sortable: true,
              className: css['strong'],
            },
            {
              accessor: 'area',
              header: tp('area'),
              className: css['strong'],
            },
            {
              accessor: `previousSeasonCropChangePlans.${
                parseInt(season) - 3
              }.crop.value`,
              header: (parseInt(season) - 3).toString(),
            },
            {
              accessor: `previousSeasonCropChangePlans.${
                parseInt(season) - 2
              }.crop.value`,
              header: (parseInt(season) - 2).toString(),
            },
            {
              accessor: `previousSeasonCropChangePlans.${
                parseInt(season) - 1
              }.crop.value`,
              header: (parseInt(season) - 1).toString(),
            },
            {
              accessor: 'crop_change_plan.crop_value',
              header: season,
              className: css['current-season'],
            },
            {
              accessor: 'crop_change_plan.breed',
              header: tp('breed'),
              className: css['current-season'],
            },
            {
              accessor: 'crop_change_plan.interculture_crop_value',
              header: tp('interculture'),
              className: css['current-season'],
            },
            {
              accessor: 'crop_change_plan.intercrop_crop_value',
              header: tp('intercrop'),
              className: css['current-season'],
            },
          ]}
          viewRoute={viewRoute}
          viewRecordAbility={{ title: 'view crop_change_plan' }}
          selectedRowsState={
            can(farmUser, { title: 'create crop_change_plan' })
              ? selectedPlanState
              : null
          }
          rowClassSetter={(row) =>
            row.crop_change_plan?.crop_work_done ? css['plan-exists'] : ''
          }
        />
      </div>
    </>
  );
}
